import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/auth';

export interface UserUpload {
  id: string;
  url: string;
  filename: string;
  createdAt: string;
  size: number;
}

interface UseUserUploadsReturn {
  uploads: UserUpload[];
  loading: boolean;
  error: string | null;
  uploadImage: (file: File) => Promise<UserUpload | null>;
  deleteImage: (fileIdentifier: string) => Promise<boolean>;
  refreshUploads: () => Promise<void>;
}

export function useUserUploads(): UseUserUploadsReturn {
  const [uploads, setUploads] = useState<UserUpload[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { session } = useAuthContext();
  const userId = session?.user?.id;

  // Function to fetch user uploads from Supabase
  const fetchUploads = useCallback(async () => {
    if (!userId) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // List all files in the user's folder
      const { data, error: listError } = await supabase.storage
        .from('user-uploads')
        .list(userId, {
          sortBy: { column: 'created_at', order: 'desc' },
        });

      if (listError) {
        throw listError;
      }

      if (!data || data.length === 0) {
        setUploads([]);
        return;
      }

      // Generate URLs for all files
      const userUploads: UserUpload[] = await Promise.all(
        data.filter(item => !item.id.endsWith('/')).map(async (file) => {
          // Use signed URLs instead of public URLs
          const { data: signedData, error: signedError } = await supabase.storage
            .from('user-uploads')
            .createSignedUrl(`${userId}/${file.name}`, 60 * 60); // 1 hour expiration
            
          if (signedError) {
            console.error('Error creating signed URL:', signedError);
            // Fallback to public URL if signing fails
            const { data: urlData } = supabase.storage
              .from('user-uploads')
              .getPublicUrl(`${userId}/${file.name}`);
                
            return {
              id: file.id,
              url: urlData.publicUrl,
              filename: file.name,
              createdAt: file.created_at,
              size: file.metadata?.size || 0,
            };
          }
          
          return {
            id: file.id,
            url: signedData.signedUrl,
            filename: file.name,
            createdAt: file.created_at,
            size: file.metadata?.size || 0,
          };
        })
      );

      setUploads(userUploads);
    } catch (err) {
      console.error('Error fetching user uploads:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user uploads');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Function to upload a new image
  const uploadImage = async (file: File): Promise<UserUpload | null> => {
    if (!userId) {
      setError('User not authenticated');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare the file path
      const filePath = `${userId}/${Date.now()}-${file.name}`;
      
      // Upload the file
      const { error: uploadError } = await supabase.storage
        .from('user-uploads')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Get a signed URL for the uploaded file
      const { data: signedData, error: signedError } = await supabase.storage
        .from('user-uploads')
        .createSignedUrl(filePath, 60 * 60); // 1 hour expiration
      
      if (signedError) {
        console.error('Error creating signed URL:', signedError);
        // Fallback to public URL
        const { data: urlData } = supabase.storage
          .from('user-uploads')
          .getPublicUrl(filePath);
          
        const newUpload: UserUpload = {
          id: filePath,
          url: urlData.publicUrl,
          filename: file.name,
          createdAt: new Date().toISOString(),
          size: file.size,
        };
        
        // Add the new upload to the uploads list
        setUploads(prev => [newUpload, ...prev]);
        
        return newUpload;
      }

      // Create a new UserUpload object with the signed URL
      const newUpload: UserUpload = {
        id: filePath,
        url: signedData.signedUrl,
        filename: file.name,
        createdAt: new Date().toISOString(),
        size: file.size,
      };

      // Add the new upload to the uploads list
      setUploads(prev => [newUpload, ...prev]);

      return newUpload;
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Function to delete an image
  const deleteImage = async (fileIdentifier: string): Promise<boolean> => {
    if (!userId) {
      setError('User not authenticated');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Find the upload object that matches the ID
      const uploadToDelete = uploads.find(upload => upload.id === fileIdentifier);
      
      if (!uploadToDelete) {
        console.error('File not found in uploads list:', fileIdentifier);
        return false;
      }
      
      // Construct the correct path using userId and the filename
      const filePath = `${userId}/${uploadToDelete.filename}`;
      
      // Delete the file
      const { error: deleteError } = await supabase.storage
        .from('user-uploads')
        .remove([filePath]);

      if (deleteError) {
        console.error('Supabase delete error:', deleteError);
        throw deleteError;
      }

      // Remove the deleted upload from the uploads list
      setUploads(prev => prev.filter(upload => upload.id !== fileIdentifier));

      // Refresh uploads to ensure the list is up to date
      await fetchUploads();
      
      return true;
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete image');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fetch uploads when the component mounts or when the user changes
  useEffect(() => {
    if (userId) {
      fetchUploads();
    }
  }, [userId, fetchUploads]);

  return {
    uploads,
    loading,
    error,
    uploadImage,
    deleteImage,
    refreshUploads: fetchUploads
  };
} 